/**
 * Cursor utilities for contentEditable elements.
 *
 * Shared between ChecklistItem and ChecklistContainer for
 * rich-text-like cursor management (split/merge/focus/navigation).
 */

/** Get the cursor offset inside a plain-text contentEditable element. */
export function getCursorOffset(el: HTMLElement): number {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return 0
  const range = sel.getRangeAt(0)
  const preRange = document.createRange()
  preRange.selectNodeContents(el)
  preRange.setEnd(range.startContainer, range.startOffset)
  return preRange.toString().length
}

/** Set cursor to a specific plain-text offset inside a contentEditable. */
export function setCursorOffset(el: HTMLElement, offset: number) {
  const sel = window.getSelection()
  if (!sel) return
  const range = document.createRange()

  // Walk text nodes to find the right one
  let remaining = offset
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    const len = node.textContent?.length ?? 0
    if (remaining <= len) {
      range.setStart(node, remaining)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
      return
    }
    remaining -= len
    node = walker.nextNode()
  }

  // Fallback: end of element
  range.selectNodeContents(el)
  range.collapse(false)
  sel.removeAllRanges()
  sel.addRange(range)
}

/**
 * True when the element holds anything other than a single plain text node.
 *
 * Enter is deliberately not prevented in checklist rows (MOB-02), so the
 * browser inserts its own line break — a <br>, or a wrapper <div>.
 * textContent does NOT see a <br>, so comparing text alone reported "nothing
 * changed" and the break survived: the row rendered an empty first line with
 * the text beneath it, doubling its height.
 */
export function hasNonTextChildren(el: HTMLElement): boolean {
  if (el.childNodes.length > 1) return true
  const first = el.firstChild
  return first != null && first.nodeType !== Node.TEXT_NODE
}

/**
 * Remove any line break the browser put into a single-line checklist row,
 * keeping the caret where it was in the text. Returns true when it changed
 * the DOM.
 *
 * The Enter handlers repair the row one frame after the keypress. On iOS the
 * keyboard delivers the line break separately from the keydown — after that
 * frame when autocorrect has a word to settle first — so the repair found
 * nothing and the break stayed, leaving an empty line under the row's text
 * (TestFlight 3.0.12 build 42880). Called from the row's input handler, this
 * catches the break whenever it arrives.
 */
export function stripLineBreaks(el: HTMLElement): boolean {
  const raw = el.textContent ?? ""
  if (!hasNonTextChildren(el) && !raw.includes("\n")) return false

  const focused = document.activeElement === el
  const caret = focused ? getCursorOffset(el) : 0
  const breaksBeforeCaret = raw.slice(0, caret).split("\n").length - 1
  el.textContent = raw.replace(/\n/g, "")
  if (focused) setCursorOffset(el, caret - breaksBeforeCaret)
  return true
}

// ---------------------------------------------------------------------------
// Arrow-key navigation helpers
//
// Used to detect when the cursor is at the visual edge of a
// contentEditable and to place the cursor in an adjacent element
// while preserving the horizontal (X) position — same technique
// ProseMirror uses (getClientRects + caretRangeFromPoint).
// ---------------------------------------------------------------------------

/**
 * Get the pixel rect of the current collapsed cursor.
 * Returns null when no selection or selection is not collapsed.
 */
export function getCursorRect(): DOMRect | null {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null
  const range = sel.getRangeAt(0)
  // getClientRects() on a collapsed range returns a single zero-width rect
  const rects = range.getClientRects()
  if (rects.length > 0) return rects[0] ?? null
  // Fallback: getBoundingClientRect (may be 0×0 on empty elements)
  return range.getBoundingClientRect()
}

/**
 * Is the cursor on the first visual line of the element?
 * Uses ProseMirror's heuristic: compare cursor Y to the element's
 * child rects to see if anything is significantly above the cursor.
 */
export function isCursorOnFirstLine(el: HTMLElement): boolean {
  const cursorRect = getCursorRect()
  if (!cursorRect || cursorRect.height === 0) return true
  const elRect = el.getBoundingClientRect()
  // If cursor top is within one line height of the element top → first line
  return cursorRect.top - elRect.top < cursorRect.height * 1.5
}

/**
 * Is the cursor on the last visual line of the element?
 */
export function isCursorOnLastLine(el: HTMLElement): boolean {
  const cursorRect = getCursorRect()
  if (!cursorRect || cursorRect.height === 0) return true
  const elRect = el.getBoundingClientRect()
  // If cursor bottom is within one line height of the element bottom → last line
  return elRect.bottom - cursorRect.bottom < cursorRect.height * 1.5
}

/**
 * Cross-browser caretFromPoint — returns the DOM node + offset at (x, y).
 * Uses caretPositionFromPoint (Firefox, Chrome 128+) or
 *      caretRangeFromPoint (Chrome, Safari, iOS Safari).
 */
function caretFromPoint(
  x: number,
  y: number,
): { node: Node; offset: number } | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const doc = document as any
  if (doc.caretPositionFromPoint) {
    const pos = doc.caretPositionFromPoint(x, y)
    if (pos) return { node: pos.offsetNode, offset: pos.offset }
  }
  if (doc.caretRangeFromPoint) {
    const range = doc.caretRangeFromPoint(x, y)
    if (range) return { node: range.startContainer, offset: range.startOffset }
  }
  return null
}

/**
 * Focus `el` and place the cursor at the character position closest
 * to the given pixel X coordinate on a specific visual line.
 *
 * @param el        Target contentEditable element
 * @param x         Desired pixel X position
 * @param fromDir   "above" → target the LAST visual line;
 *                  "below" → target the FIRST visual line
 */
export function focusAtXPosition(
  el: HTMLElement,
  x: number,
  fromDir: "above" | "below",
) {
  // preventScroll — see the note in ChecklistContainer: the browser's own
  // focus-scroll overshoots badly and makes the card lurch. Callers scroll
  // the row into view themselves, minimally.
  el.focus({ preventScroll: true })
  const elRect = el.getBoundingClientRect()

  // Target Y: slightly inside the first or last line
  const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20
  const targetY =
    fromDir === "above"
      ? elRect.bottom - lineHeight * 0.5 // last line
      : elRect.top + lineHeight * 0.5 // first line

  // Clamp X to within the element bounds
  const clampedX = Math.max(elRect.left + 1, Math.min(x, elRect.right - 1))

  const caret = caretFromPoint(clampedX, targetY)
  if (caret && el.contains(caret.node)) {
    const sel = window.getSelection()
    if (sel) {
      const range = document.createRange()
      range.setStart(caret.node, caret.offset)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
    }
    return
  }

  // Fallback: start or end
  setCursorOffset(el, fromDir === "above" ? (el.textContent?.length ?? 0) : 0)
}
